import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Moon, Sun } from "lucide-react";
import { cn } from "../../../lib/utils";
import type { Pwf } from "../../../api/pwf";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";

/** "Waking {X} asks one of the three to rest — which one?" (spec §3.b.4).
 *  Mount with key={wake._id} so the pick resets per project. */
export const SwapDialog = ({
  wake,
  active,
  busy,
  onCancel,
  onConfirm,
}: {
  wake: Pwf | null;
  active: Pwf[];
  busy: boolean;
  onCancel: () => void;
  onConfirm: (rest: Pwf) => void;
}) => {
  const { t } = useTranslation();
  const [restId, setRestId] = useState<string | null>(null);
  const rest = active.find((p) => p._id === restId) ?? null;

  return (
    <Dialog open={Boolean(wake)} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base leading-snug">
            {wake ? t("cockpit_swap_title", { title: wake.title }) : ""}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[13px] text-muted-foreground -mt-1">{t("cockpit_swap_which")}</p>
        <div className="space-y-2" role="radiogroup" aria-label={t("cockpit_swap_which")}>
          {active.map((p) => {
            const picked = restId === p._id;
            return (
              <button
                key={p._id}
                type="button"
                role="radio"
                aria-checked={picked}
                onClick={() => setRestId(p._id)}
                className={cn(
                  "w-full text-start min-h-[44px] rounded-xl border px-4 py-2.5 flex items-center gap-3 transition-colors",
                  picked ? "border-primary bg-primary/5" : "border-border/70 hover:border-border"
                )}
              >
                {picked ? (
                  <Moon className="w-4 h-4 text-primary flex-none" />
                ) : (
                  <Sun className="w-4 h-4 text-amber-500 flex-none" />
                )}
                <span className="text-sm text-foreground line-clamp-1">{p.title}</span>
              </button>
            );
          })}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" className="rounded-full min-h-[44px]" onClick={onCancel} disabled={busy}>
            {t("cockpit_swap_cancel")}
          </Button>
          <Button
            className="rounded-full min-h-[44px]"
            disabled={!rest || busy}
            onClick={() => rest && onConfirm(rest)}
          >
            {rest && wake
              ? t("cockpit_swap_confirm", { rest: rest.title, wake: wake.title })
              : t("cockpit_swap_confirm_pick")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
